import { useEffect } from "react";
import { FiArrowLeft, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AssetsTab } from "@/features/worksheets/components/tabs/assets-tab";
import { BankingTab } from "@/features/worksheets/components/tabs/banking-tab";
import { FinancialTab } from "@/features/worksheets/components/tabs/financial-tab";
import { IncexpTab } from "@/features/worksheets/components/tabs/incexp-tab";
import { PersonalTab } from "@/features/worksheets/components/tabs/personal-tab";
import { GenerateStep } from "@/features/workflows/components/steps/generate-step";
import { SummaryStep } from "@/features/workflows/components/steps/summary-step";
import { TermsStep } from "@/features/workflows/components/steps/terms-step";
import { WorkflowRail } from "@/features/workflows/components/workflow-rail";
import { useIaWorkflow } from "@/features/workflows/hooks/use-ia-workflow";
import {
  IA_STEPS,
  stepIndex,
  type SelectedForm,
  type WorkflowDetermination,
  type WorkflowStep,
} from "@/features/workflows/types";
import { useActiveClient } from "@/layouts/client-layout";
import { useState } from "react";

const STEP_KEYS = IA_STEPS.map((s) => s.key);
const isStep = (v: string | undefined): v is WorkflowStep =>
  !!v && (STEP_KEYS as string[]).includes(v);

/**
 * Guided Installment-Agreement workflow (milestone 03-13). A stepper over the
 * Tier 2.2 worksheet tabs (steps 1–5), the Tier 2.2 determination (step 6), the
 * Tier 3 433-H/433-D terms editor (step 7), and the Tier 3 generate flow (step 8).
 * The `:step` URL param owns the active step so deep-linking + refresh work.
 */
export default function IAWorkflowPage() {
  const { client } = useActiveClient();
  const { caseId, step } = useParams<{ caseId: string; step: string }>();
  const navigate = useNavigate();
  const { workflow, loading, error, persistStep, selectForm, setWorkflow } =
    useIaWorkflow(caseId!);
  const [determination, setDetermination] = useState<WorkflowDetermination | null>(null);

  const base = `/client/${client.id}/cases/${caseId}/workflow/ia`;
  const active: WorkflowStep = isStep(step) ? step : "personal";

  // Resume: landing on the base (no/invalid step) routes to the saved step.
  useEffect(() => {
    if (!isStep(step) && workflow) {
      navigate(`${base}/${workflow.currentStep}`, { replace: true });
    }
    // base is derived from stable ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, workflow]);

  // Persist the active step as the resume target whenever it changes.
  useEffect(() => {
    if (workflow && isStep(step)) void persistStep(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, workflow]);

  if (loading) return <Loader className="mt-16" />;
  if (error || !workflow) {
    return (
      <Alert variant="destructive" className="mt-8">
        <AlertTitle>Couldn't start the workflow</AlertTitle>
        <AlertDescription>{error ?? "Please try again."}</AlertDescription>
      </Alert>
    );
  }

  const idx = stepIndex(active);
  const goto = (s: WorkflowStep) => navigate(`${base}/${s}`);
  const selectedForm: "433-H" | "433-D" =
    workflow.selectedForm === "433-D" ? "433-D" : "433-H";

  async function chooseForm(form: SelectedForm) {
    if (form === "433-H" || form === "433-D") await selectForm(form);
  }

  function renderStep() {
    switch (active) {
      case "financial":
        return <FinancialTab caseId={caseId!} />;
      case "assets":
        return <AssetsTab caseId={caseId!} />;
      case "banking":
        return <BankingTab caseId={caseId!} />;
      case "incexp":
        return <IncexpTab caseId={caseId!} />;
      case "summary":
        return (
          <SummaryStep
            caseId={caseId!}
            workflowId={workflow!.id}
            onDetermination={setDetermination}
            onChoosePlan={(form) => {
              void chooseForm(form);
              goto("agreement");
            }}
            onChooseOic={() => navigate(`/client/${client.id}/cases/${caseId}/oic`)}
          />
        );
      case "agreement":
        return (
          <TermsStep
            client={client}
            caseId={caseId!}
            selectedForm={selectedForm}
            onSelectForm={(f) => void chooseForm(f)}
          />
        );
      case "generate":
        return (
          <GenerateStep
            client={client}
            caseId={caseId!}
            workflow={workflow!}
            onCompleted={setWorkflow}
          />
        );
      default:
        return <PersonalTab caseId={caseId!} clientId={client.id} client={client} />;
    }
  }

  return (
    <div className="grid gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/client/${client.id}/cases/${caseId}`}>
            <FiArrowLeft className="size-4" /> Back to case
          </Link>
        </Button>
        <h2 className="text-xl font-semibold">Installment Agreement Workflow</h2>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <WorkflowRail
          currentStep={active}
          onSelect={goto}
          determination={determination ?? workflow.determination}
        />

        <div className="grid gap-6">
          <div>
            <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Step {idx + 1} of {IA_STEPS.length}
            </p>
            <h3 className="text-lg font-semibold">{IA_STEPS[idx]!.label}</h3>
          </div>

          {renderStep()}

          <div className="flex items-center justify-between border-t pt-4">
            <Button
              variant="outline"
              disabled={idx === 0}
              onClick={() => goto(IA_STEPS[idx - 1]!.key)}
            >
              <FiChevronLeft className="size-4" /> Back
            </Button>
            <Button
              disabled={idx === IA_STEPS.length - 1}
              onClick={() => goto(IA_STEPS[idx + 1]!.key)}
            >
              Next <FiChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
